import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useMediaQuery,
  Dialog,
  Card,
} from "@mui/material";
import { styled } from "@mui/system";
import CustomerForm from "./CustomerForm";
import Component from "./Component";
import ButtonGroupComponent from "./ButtonGroupComponent";
import HeaderSection from "./HeaderSection";
import axios from "axios";
import logox from './assets/MyImg/LoginImgy.png';
import EditIcon from '@mui/icons-material/Edit';

const OuterBox = styled(Box)({
  width: "31%",
  height: "70%",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  marginLeft: "-1000px",
  padding: "16px",
  boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  marginTop: "-5px", // Added some margin-top to give space from top
});

const InvoiceHeaderBox = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "10px",
});

const ContentTable = styled(TableContainer)({
  backgroundColor: "white",
  borderRadius: "8px",
  overflowY: "auto",
  maxHeight: "280px",
  overflow: "hidden",
});

const HeaderRow = styled(TableRow)({

  "& > *": {
    color: "white",
    backgroundColor: "black",
    fontWeight: "bold",
    padding: "10px",
    marginTop: "-10px"
  },
});

const TotalRowContainer = styled(Box)({
  backgroundColor: "#20a4a5",
  padding: "10px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderRadius: "5px",
  marginTop: "10px",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  flexDirection: "column", // Stack buttons in two rows
  justifyContent: "center",
  marginLeft: "-1020px",
  width: "31%", // Set a width for the buttons container
  marginTop: "20px", // Add space between invoice box and buttons
  gap: "7px", // Add gap between rows
  alignItems: "center",
  // Center the buttons horizontally
});

const Invoice = () => {
  const isMobileOrTab = useMediaQuery("(max-width:1024px)");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState(""); // Store Invoice Number
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [clickedRow] = useState(null); // Track clicked row index
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDoneClicked, setIsDoneClicked] = useState(false); // Track DONE button click
  const [highlightProduct, setHighlightProduct] = useState(null); // <-- Add this
  const [popup, setPopup] = useState({ open: false, message: "" }); // Add popup state

  const deliveryOptions = ["Option", "Takeaway", "Delivery"];
  const paymentOptions = ["Cash", "Card", "Credit"];
  const handleSaveInvoice = async () => {
    if (isDoneClicked) return;
    setIsDoneClicked(true);

    if (selectedProducts.length === 0) return;

    const invoiceData = {
      items: selectedProducts.map((product) => ({
        item: product.name,
        quantity: product.quantity,
        price: product.price,
        vat: product.vat,
        total: product.price * product.quantity,
      })),
      overallTotal: selectedProducts.reduce((sum, p) => sum + p.price * p.quantity, 0),
    };

    try {
      // Save the invoice
      const response = await axios.post("https://enzo-matr.onrender.com/invoices", invoiceData);

      // Update product counts in the backend
      await Promise.all(
        selectedProducts.map((product) =>
          axios.put(`https://enzo-matr.onrender.com/api/products/${product._id}/reduce-count`, {
            quantity: product.quantity,
          })
        )
      );

      if (response.data.invoiceNumber) {
        setInvoiceNo(`INV-${response.data.invoiceNumber}`);
        console.log("✅ Invoice stored successfully!");
        setPopup({
          open: true,
          message: "Invoice saved successfully. Invoice Number: INV-" + response.data.invoiceNumber,
        });
      } else {
        setPopup({
          open: true,
          message: "Invoice saved, but no invoice number received.",
        });
      }
      // Do not clear selectedProducts to keep the data in the table
    } catch (error) {
      console.error("Error saving invoice:", error);
      setPopup({
        open: true,
        message: "Error saving invoice.",
      });
    }
  };

  const handleRemoveProduct = (indexToRemove) => {
    setSelectedProducts((prevProducts) =>
      prevProducts.filter((_, index) => index !== indexToRemove)
    );
  };

  const clearInvoice = () => {
    setSelectedProducts([]);
  };

  const handleAddProduct = (product) => {
    setSelectedProducts((prevProducts) => {
      const existingProductIndex = prevProducts.findIndex((p) => p.name === product.name);
      if (existingProductIndex !== -1) {
        const updatedProducts = [...prevProducts];
        updatedProducts[existingProductIndex].quantity = product.quantity;
        updatedProducts[existingProductIndex].total = product.quantity * product.price;
        return updatedProducts;
      } else {
        return [...prevProducts, { ...product, total: product.quantity * product.price }];
      }
    });
  };

  useEffect(() => {
    const newTotal = selectedProducts.reduce((sum, product) => sum + product.total, 0);
    setTotalPrice(newTotal);
  }, [selectedProducts]);

  const handlePrint = async () => {
    const invoiceData = {
      invoiceNo: invoiceNo || `INV-${Date.now()}`,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      totalItems: selectedProducts.length,
      products: selectedProducts,
      totalPrice: totalPrice.toFixed(2),
      deliveryType: selectedDelivery || "N/A",
      paymentType: selectedPayment || "N/A",
      customerName: selectedCustomer?.name || "",
      customerId: selectedCustomer?.idNumber || "",
      customerState: selectedCustomer?.state || "",
    };

    console.log("📤 Sending Invoice Data:", invoiceData);

    try {
      const response = await fetch("https://enzo-matr.onrender.com/invoice_bill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(invoiceData),
      });

      const responseData = await response.json();
      console.log("✅ Server Response:", responseData);

      if (!response.ok) {
        throw new Error(responseData.error || "Failed to store invoice");
      }

      console.log("✅ Invoice stored successfully!");
      alert("Invoice saved successfully!");

      const printContents = `
      <div style="text-align: center; width: 100%; margin-top: 50px;">
        <div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
          <img src="${logox}" alt="Logo" style="width: 60px; height: 35px; margin-top:-15px" />
          <h6 style="font-family: 'source serif pro'; font-size: 34px; font-weight: bold; margin: 0;">
            Enzo Management
          </h6>
        </div>
        <p style="font-family: 'cardo'; font-size: 14px; margin: 5px 0;">Life Changes Ind.</p>
      </div>
      
      <div style="display: flex; justify-content: space-between; width: 50%; margin: auto; font-size: 14px; margin-bottom: 10px;">
        <div style="text-align: left;">
          <p>Invoice No: <span>${invoiceData.invoiceNo}</span></p>
          <p>Total Items: <span>${selectedProducts.length}</span></p>
        </div>
         <div style="text-align: right;">
          <p>Customer Name: <span>${invoiceData.customerName}</span></p>
          <p>Customer ID: <span>${invoiceData.customerId}</span></p>
        </div>
        <div style="text-align: right;">
          <p>Date: <span>${invoiceData.date}</span></p>
          <p>Time: <span>${invoiceData.time}</span></p>
        </div>
      </div>

      <div style="max-width: 50%; margin: auto; border-collapse: collapse; text-align: center;">
        <div style="display: flex; background: #eeecec; font-weight: bold; padding: 10px; border-bottom: 1px solid;">
          <div style="width: 30%; text-align: left;">Item</div>
          <div style="width: 15%;">Qty</div>
          <div style="width: 15%;">VAT</div>
          <div style="width: 20%;">Rate</div>
          <div style="width: 20%;">Amount</div>
        </div>
        ${selectedProducts.map(product => `
          <div style="display: flex; border-bottom: 1px solid; padding: 8px;">
            <div style="width: 30%; text-align: left;">${product.name}</div>
            <div style="width: 15%;">${product.quantity}</div>
            <div style="width: 15%;">${product.vat}%</div>
            <div style="width: 20%;">$${product.price.toFixed(2)}</div>
            <div style="width: 20%;">$${(product.price * product.quantity).toFixed(2)}</div>
          </div>
        `).join('')}

        <h6 style="text-align: right; border-top: 1px solid; padding-top: 8px; font-size: 16px; margin-top: 10px;">
          Total: <span>$${totalPrice.toFixed(2)}</span>
        </h6>
      </div>

      <div style="max-width: 50%; margin: auto; margin-top: 10px; font-weight: bold; font-size: 14px;">
        <p>Delivery: <span>${selectedDelivery}</span><br><br>  
        Payment: <span>${selectedPayment}</span></p>
      </div>
    `;

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`<html><head><title>Print Invoice</title></head><body>${printContents}</body></html>`);
      printWindow.document.close();
      printWindow.print();

    } catch (error) {
      console.error("❌ Error storing invoice:", error);
      alert(`Error storing invoice: ${error.message}`);
    }
  };

  const handleShare = () => {
    const textToShare = `Enzo Management\nLife Changes Ind.\n
    Invoice No: ${invoiceNo}\n
    Total Items: ${selectedProducts.length}\n
    Date: ${new Date().toLocaleDateString()}\n
    ${selectedProducts.map(product => `
      ${product.item} - Qty: ${product.quantity}, VAT: ${product.vat}%, Rate: $${product.price.toFixed(2)}, Amount: $${(product.price * product.quantity).toFixed(2)}
    `).join('\n')}
    Delivery: ${selectedDelivery}, Payment: ${selectedPayment}
    Total: $${totalPrice.toFixed(2)}`;

    const encodedText = encodeURIComponent(textToShare);

    const whatsappUrl = `https://wa.me/?text=${encodedText}`;
    const emailSubject = encodeURIComponent("Invoice Details");
    const emailBody = encodedText.replace(/\n/g, "%0D%0A");
    const mailtoUrl = `mailto:?subject=${emailSubject}&body=${emailBody}`;
    const telegramUrl = `https://t.me/share/url?url=${encodedText}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;

    setTimeout(() => {
      window.open(whatsappUrl, "_blank");
      window.open(mailtoUrl, "_blank");
      window.open(telegramUrl, "_blank");
      window.open(twitterUrl, "_blank");
    }, 500);

    if (navigator.share) {
      navigator.share({
        title: "Invoice Details",
        text: textToShare,
      })
        .then(() => console.log("Shared successfully"))
        .catch((error) => console.error("Error sharing:", error));
    }
  };

  const handleQuantityChange = (index, change) => {
    setSelectedProducts((prevProducts) =>
      prevProducts.map((product, i) =>
        i === index
          ? {
            ...product,
            quantity: Math.max(0, Math.min(product.quantity + change, product.count)), // Ensure quantity is between 0 and count
            total: Math.max(0, Math.min(product.quantity + change, product.count)) * product.price,
          }
          : product
      )
    );
  };

  return (
    <>
      <HeaderSection />
      <Box
        sx={{
          bgcolor: "#20a4a3",
          p: 1,
          textAlign: "center",
          marginBottom: "0px",
          marginTop: "-10px",
          position: "relative"
        }}
      >
        <Typography variant={isMobileOrTab ? "h6" : "h4"} sx={{ fontWeight: "bold", color: "white", fontFamily: "caladea", }}>
          INVOICES
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          width: isMobileOrTab ? "auto":"100vw",
          height: isMobileOrTab ? "auto" : "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          marginTop: "-0px"
        }}
      >
        <OuterBox sx={{
          width: isMobileOrTab ? "90vw" : "31%",
          height: isMobileOrTab ? "auto" : "70%",
          marginLeft: isMobileOrTab ? 0 : "-1000px",
          marginTop: isMobileOrTab ? 1 : "-5px",
        }}>
          <InvoiceHeaderBox>
            <Box sx={{ color: "black" }}>
              <Typography variant="body1">
                Invoice No: {isDoneClicked ? (invoiceNo || "loading...") : "loading..."}
              </Typography>
              <Typography variant="body2">Item Count: {selectedProducts.length}</Typography>
            </Box>
          </InvoiceHeaderBox>

          <ContentTable component={Paper} sx={{
            overflow: "auto", marginTop: "px", height: "300px", scrollbarWidth: "none",
            "-ms-overflow-style": "none",
            "&::-webkit-scrollbar": { display: "none" }
          }}>
            <Table stickyHeader>
              <TableHead>
                <HeaderRow>
                  <TableCell sx={{ color: "white", backgroundColor: "black" }}>Item</TableCell>
                  <TableCell sx={{ color: "white", backgroundColor: "black" }}>Qty</TableCell>
                  <TableCell sx={{ color: "white", backgroundColor: "black" }}>Price</TableCell>
                  <TableCell sx={{ color: "white", backgroundColor: "black" }}>VAT</TableCell>
                  <TableCell sx={{ color: "white", backgroundColor: "black" }}>Total</TableCell>
                </HeaderRow>
              </TableHead>
              <TableBody>
                {selectedProducts.map((product, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          width: "13px",
                          height: "18px",
                          backgroundColor: "red",
                          fontSize: "12px",
                          textAlign: "center",
                          borderRadius: "2px",
                          marginRight: "8px",
                          color: "white",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => handleRemoveProduct(index)}
                      >X</Box>
                      {/* Edit icon next to X */}
                      <EditIcon
                        sx={{
                          fontSize: 18,
                          color: "#1976d2",
                          cursor: "pointer",
                          verticalAlign: "middle",
                          marginLeft: "6px",
                          marginRight: "6px"
                        }}
                        onClick={() => {
                          setHighlightProduct(product.name); // <-- Set product to highlight
                        }}
                      />
                      {product.name}
                    </TableCell>
                    <TableCell align="center">{product.quantity}</TableCell>
                    <TableCell>{product.price}</TableCell>
                    <TableCell>{product.vat}</TableCell>
                    <TableCell>{product.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ContentTable>
          <TotalRowContainer>
            <Box sx={{ display: "flex", justifyContent: "space-around", mt: 0 }}>
              <Button
                sx={{ background: "black" }}
                variant="contained"
                onClick={handleSaveInvoice}
                disabled={isDoneClicked || !selectedDelivery || !selectedPayment} // Disable button after click or if conditions are not met
              >
                DONE
              </Button>
              <Button sx={{ background: "black", ml: "20px" }} variant="contained" onClick={clearInvoice}>
                Clear
              </Button>
            </Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", color: "black", marginLeft: "100px" }}
            >
              Total:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: "black",
                background: "white",
                width: "80px",
                textAlign: "center",
                borderRadius: "5px",
                border: "2px solid black",
              }}
            >
              {totalPrice}
            </Typography>
          </TotalRowContainer>
        </OuterBox>

        <ButtonContainer sx={{
          marginLeft: isMobileOrTab ? 0 : "-1020px",
          width: isMobileOrTab ? "98vw" : "31%",
          marginTop: isMobileOrTab ? 1 : "20px",
        }}>
          <Box>
            <Box
              display="flex"
              justifyContent={isMobileOrTab ? "center" : "space-between"}
              mb={1}
              gap={isMobileOrTab ? 1 : 1}
              flexWrap={isMobileOrTab ? "wrap" : "nowrap"}
            >
              {deliveryOptions.map((option) => (
                <Button
                  key={option}
                  variant="contained"
                  sx={{
                    width: isMobileOrTab ? "32vw" : "150px",
                    minWidth: isMobileOrTab ? "90px" : "150px",
                    maxWidth: isMobileOrTab ? "228px" : "150px",
                    height: "45px",
                    backgroundColor: selectedDelivery === option ? "#f39c12" : "#1E201E",
                    color: selectedDelivery === option ? "black" : "white",
                    textTransform: "none",
                    fontFamily: "caladea",
                    fontWeight: "bold",
                    fontSize: isMobileOrTab ? 16 : 20,
                    border: "2px solid black",
                    borderRadius: "8px",
                  }}
                  onClick={() => setSelectedDelivery(option)}
                >
                  {option}
                </Button>
              ))}
            </Box>

            <Box
              display="flex"
              justifyContent={isMobileOrTab ? "center" : "space-between"}
              gap={isMobileOrTab ? 1 : 1}
              flexWrap={isMobileOrTab ? "wrap" : "nowrap"}
            >
              {paymentOptions.map((option) => (
                <Button
                  key={option}
                  variant="contained"
                  sx={{
                    width: isMobileOrTab ? "32vw" : "150px",
                    minWidth: isMobileOrTab ? "90px" : "150px",
                    maxWidth: isMobileOrTab ? "228px" : "150px",
                    height: "45px",
                    backgroundColor: selectedPayment === option ? "#f39c12" : "#20a4a5",
                    color: "black",
                    textTransform: "none",
                    fontFamily: "caladea",
                    fontWeight: "bold",
                    fontSize: isMobileOrTab ? 16 : 20,
                    border: "2px solid black",
                    borderRadius: "8px",
                  }}
                  onClick={() => setSelectedPayment(option)}
                >
                  {option}
                </Button>
              ))}
            </Box>
          </Box>
        </ButtonContainer>
        <Component
          onAddProduct={handleAddProduct}
          highlightProduct={highlightProduct} // <-- Pass as prop
        />
        <CustomerForm
          totalPrice={totalPrice}
          setTotalPrice={setTotalPrice}
          onSelectCustomer={setSelectedCustomer} />
        <ButtonGroupComponent
          onPrint={handlePrint}
          onShare={handleShare}
          isDoneClicked={isDoneClicked}
        />
      </Box>

      <Dialog
        open={popup.open}
        onClose={() => setPopup({ ...popup, open: false })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", textAlign: "center", p: 2 }
        }}
      >
        <Card sx={{ borderRadius: 3, p: 2, minWidth: 300, textAlign: "center" }}>
          <Box display="flex" flexDirection="column" alignItems="center" mt={1}>
            {/* Green Tick SVG */}
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="12" fill="#4caf50"/>
              <path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <Typography
              variant="h6"
              align="center"
              sx={{
                fontWeight: "bold",
                color: "#4caf50",
                mt: 2
              }}
            >
              {popup.message}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setPopup({ ...popup, open: false })}
              sx={{
                background: "#4caf50",
                color: "white",
                fontWeight: "bold",
                mt: 2
              }}
            >
              OK
            </Button>
          </Box>
        </Card>
      </Dialog>
    </>
  );
};

export default Invoice;
